import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  Popover,
  Tooltip,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleTwoToneIcon from '@material-ui/icons/CheckCircleTwoTone';
import BugReportTwoToneIcon from '@material-ui/icons/BugReportTwoTone';
import SecurityTwoToneIcon from '@material-ui/icons/SecurityTwoTone';
import AssessmentTwoToneIcon from '@material-ui/icons/AssessmentTwoTone';
import AssignmentTurnedInTwoToneIcon from '@material-ui/icons/AssignmentTurnedInTwoTone';
import {
  MarketplaceMetricsDTO,
  WeakPointSeverity,
} from '../../DTO/MarketplaceMetrics';

interface MetricsDisplayProps {
  metrics: MarketplaceMetricsDTO;
}

const WEAK_POINT_SEVERITY_COLOR: Record<WeakPointSeverity, string> = {
  low: '#1976d2',
  medium: '#f57c00',
  high: '#d32f2f',
};

const WEAK_POINT_SEVERITY_LABEL: Record<WeakPointSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const WEAK_POINT_SEVERITY_ORDER: WeakPointSeverity[] = ['high', 'medium', 'low'];
const COVERAGE_THRESHOLD_COLOR = {
  below: '#d32f2f',
  above: '#2e7d32',
};

const MetricsDisplay: React.FC<MetricsDisplayProps> = ({ metrics }) => {
  const [vulnerabilitiesInfoAnchor, setVulnerabilitiesInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [weakPointsInfoAnchor, setWeakPointsInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [qualityInfoAnchor, setQualityInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [securityInfoAnchor, setSecurityInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [dependenciesInfoAnchor, setDependenciesInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [agenticInfoAnchor, setAgenticInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [architecturalFitnessInfoAnchor, setArchitecturalFitnessInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [documentationScoreInfoAnchor, setDocumentationScoreInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [packageComplianceInfoAnchor, setPackageComplianceInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [overallComplianceInfoAnchor, setOverallComplianceInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const coverage = metrics.coverage ?? {
    total: 0,
    withCoverage: 0,
    averageCoverage: null,
    withCoverageThreshold: 0,
    aboveThreshold: 0,
    belowThreshold: 0,
  };
  const security = metrics.security ?? {
    total: 0,
    withSastReport: 0,
    withSecretDetection: 0,
    averageSastWarnings: null,
    averageSecretDetectionWarnings: null,
  };
  const dependencies = metrics.dependencies ?? {
    total: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    averageCritical: null,
    averageHigh: null,
    averageMedium: null,
    averageLow: null,
  };
  const agenticEvaluator = metrics.agenticEvaluator ?? {
    withDocumentationScore: 0,
    withArchitecturalFitness: 0,
    withWeakPointCounters: 0,
    averageDocumentationScore: null,
    averageArchitecturalFitness: null,
    weakPointCounters: [],
    weakPointCategories: [],
  };
  const packageCompliance = metrics.packageCompliance ?? {
    total: 0,
    low: 0,
    medium: 0,
    high: 0,
    averageLow: null,
    averageMedium: null,
    averageHigh: null,
  };

  return (
    <Box mt={3}>
      {/* Total Projects */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography gutterBottom>Total Projects</Typography>
              <Typography variant="h4">{metrics.totalProjects}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    {/* Coverage */}
    <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
      <CheckCircleTwoToneIcon fontSize="small" style={{ marginRight: 8, marginBottom: 4  }} />
      <Typography variant="h6" gutterBottom>Quality and Security</Typography>
      <IconButton
        aria-label="More information about quality and security"
        size="small"
        style={{ marginLeft: 4, marginBottom: 4 }}
        onClick={(event) => setQualityInfoAnchor(event.currentTarget)}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(qualityInfoAnchor)}
        anchorEl={qualityInfoAnchor}
        onClose={() => setQualityInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Typography style={{ padding: 16, maxWidth: 360 }}>
          This section shows the test coverage quality of your projects. It tracks how many projects have an automatic test report available, and among those, how many meet the configured coverage threshold. A high percentage above threshold indicates well-tested codebases.
        </Typography>
      </Popover>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography gutterBottom>Automatic Tests Report</Typography>
            <Typography variant="h4">
              {coverage.withCoverage} / {coverage.total}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {coverage.total > 0
                ? `Found on ${Math.round((coverage.withCoverage / coverage.total) * 100)}% of projects`
                : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <Card>
          <CardContent>
            <Typography gutterBottom>Test Coverage Above Threshold</Typography>
            {coverage.withCoverageThreshold > 0 ? (
              <>
                <Typography variant="h4">
                  {coverage.aboveThreshold} / {coverage.withCoverageThreshold}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Below threshold: {Math.round((coverage.belowThreshold / coverage.withCoverageThreshold) * 100)}%, Above threshold: {Math.round((coverage.aboveThreshold / coverage.withCoverageThreshold) * 100)}%
                </Typography>
                <Box
                  mt={1}
                  display="flex"
                  width="100%"
                  height={18}
                  borderRadius={9}
                  overflow="hidden"
                  style={{ backgroundColor: '#eceff1' }}
                >
                  {coverage.belowThreshold > 0 && (
                    <Tooltip title={`Below threshold: ${coverage.belowThreshold}`}>
                      <Box
                        height="100%"
                        style={{
                          width: `${(coverage.belowThreshold / coverage.withCoverageThreshold) * 100}%`,
                          backgroundColor: COVERAGE_THRESHOLD_COLOR.below,
                        }}
                      />
                    </Tooltip>
                  )}
                  {coverage.aboveThreshold > 0 && (
                    <Tooltip title={`Above threshold: ${coverage.aboveThreshold}`}>
                      <Box
                        height="100%"
                        style={{
                          width: `${(coverage.aboveThreshold / coverage.withCoverageThreshold) * 100}%`,
                          backgroundColor: COVERAGE_THRESHOLD_COLOR.above,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </>
            ) : (
              <Typography variant="h4">N/A</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {/* Security */}
    <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
      <SecurityTwoToneIcon fontSize="small" style={{ marginRight: 8, marginBottom: 4  }} />
      <Typography variant="h6" gutterBottom>Security Analysis</Typography>
      <IconButton
        aria-label="More information about security analysis"
        size="small"
        style={{ marginLeft: 4, marginBottom: 4 }}
        onClick={(event) => setSecurityInfoAnchor(event.currentTarget)}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(securityInfoAnchor)}
        anchorEl={securityInfoAnchor}
        onClose={() => setSecurityInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Typography style={{ padding: 16, maxWidth: 360 }}>
          This section shows security scanning coverage across your projects. It tracks how many projects have SAST (Static Application Security Testing) reports enabled to detect code vulnerabilities, and how many have secret detection configured to prevent accidental exposure of credentials and sensitive data.
        </Typography>
      </Popover>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography gutterBottom>SAST Reports</Typography>
            <Typography variant="h4">{security.withSastReport} / {security.total}</Typography>
            <Typography variant="caption" color="textSecondary">
              {security.total > 0
                ? `Found on ${Math.round((security.withSastReport / security.total) * 100)}% of projects`
                : 'N/A'}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Avg warnings: {security.averageSastWarnings !== null
                ? security.averageSastWarnings.toFixed(2)
                : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography gutterBottom>Secret Detection</Typography>
            <Typography variant="h4">{security.withSecretDetection} / {security.total}</Typography>
            <Typography variant="caption" color="textSecondary">
              {security.total > 0
                ? `Found on ${Math.round((security.withSecretDetection / security.total) * 100)}% of projects`
                : 'N/A'}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Avg warnings: {security.averageSecretDetectionWarnings !== null
                ? security.averageSecretDetectionWarnings.toFixed(2)
                : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {/* Dependencies */}
    <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
      <BugReportTwoToneIcon fontSize="small" style={{ marginRight: 8, marginBottom: 4  }} />
      <Typography variant="h6" gutterBottom>Dependency Vulnerabilities</Typography>
      <IconButton
        aria-label="More information about dependency vulnerabilities"
        size="small"
        style={{ marginLeft: 4, marginBottom: 4 }}
        onClick={(event) => setDependenciesInfoAnchor(event.currentTarget)}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(dependenciesInfoAnchor)}
        anchorEl={dependenciesInfoAnchor}
        onClose={() => setDependenciesInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Typography style={{ padding: 16, maxWidth: 360 }}>
          This section summarises known vulnerabilities found in third-party dependencies across your projects. It shows how many projects have a dependency scanning report and breaks down the total number of vulnerabilities by severity (Critical, High, Medium, Low), including the average per project.
        </Typography>
      </Popover>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3} >
        <Card>
          <CardContent>
            <Typography gutterBottom>Dependency Reports</Typography>
            <Typography variant="h4">
              {dependencies.total} / {metrics.totalProjects}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {metrics.totalProjects > 0
                ? `Found on ${Math.round((dependencies.total / metrics.totalProjects) * 100)}% of projects`
                : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ marginBottom: 16 }}>
            <Typography gutterBottom>Vulnerabilities by Severity</Typography>
            <IconButton
              aria-label="More information about vulnerabilities by severity"
              size="small"
              onClick={(event) => setVulnerabilitiesInfoAnchor(event.currentTarget)}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
            <Popover
              open={Boolean(vulnerabilitiesInfoAnchor)}
              anchorEl={vulnerabilitiesInfoAnchor}
              onClose={() => setVulnerabilitiesInfoAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Typography style={{ padding: 16, maxWidth: 320 }}>
                The summary demonstrates the breakdown of vulnerabilities issues based on severity and the threat level it poses to your application.
              </Typography>
            </Popover>
          </Box>
          {(
            [
              { label: 'Critical', key: 'critical', avg: 'averageCritical', color: '#d32f2f' },
              { label: 'High', key: 'high', avg: 'averageHigh', color: '#f57c00' },
              { label: 'Medium', key: 'medium', avg: 'averageMedium', color: '#fbc02d' },
              { label: 'Low', key: 'low', avg: 'averageLow', color: '#7cb342' },
            ] as const
          ).reduce((acc, { key }) => acc + dependencies[key], 0) > 0 ? (
            <Box
              display="flex"
              alignItems="flex-end"
              justifyContent="space-around"
              style={{ minHeight: 220, paddingBottom: 12 }}
            >
              {(
                [
                  { label: 'Critical', key: 'critical', avg: 'averageCritical', color: '#d32f2f' },
                  { label: 'High', key: 'high', avg: 'averageHigh', color: '#f57c00' },
                  { label: 'Medium', key: 'medium', avg: 'averageMedium', color: '#fbc02d' },
                  { label: 'Low', key: 'low', avg: 'averageLow', color: '#7cb342' },
                ] as const
              ).map(({ label, key, avg, color }) => {
                const maxValue = Math.max(
                  dependencies.critical,
                  dependencies.high,
                  dependencies.medium,
                  dependencies.low,
                  1,
                );
                const barHeight = Math.max((dependencies[key] / maxValue) * 140, dependencies[key] > 0 ? 18 : 0);

                return (
                  <Box key={key} display="flex" flexDirection="column" alignItems="center" width="22%">
                    <Tooltip title={`${label}: ${dependencies[key]}`}>
                      <Box
                        width="60%"
                        borderRadius={4}
                        style={{ height: barHeight, backgroundColor: color, minHeight: dependencies[key] > 0 ? 18 : 0 }}
                      />
                    </Tooltip>
                    <Typography variant="h4" style={{ marginTop: 8, color }}>{dependencies[key]}</Typography>
                    <Typography variant="body1">{label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg: {dependencies[avg] !== null ? dependencies[avg]!.toFixed(2) : 'N/A'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="h4">N/A</Typography>
          )}
        </CardContent>
      </Card>
      </Grid>
    </Grid>

    {/* Package Compliance */}
    <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
      <AssignmentTurnedInTwoToneIcon style={{ marginRight: 8, marginBottom: 4 }} />
      <Typography variant="h6" gutterBottom>Package Compliance</Typography>
      <IconButton
        aria-label="More information about package compliance"
        size="small"
        style={{ marginLeft: 4, marginBottom: 4 }}
        onClick={(event) => setPackageComplianceInfoAnchor(event.currentTarget)}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(packageComplianceInfoAnchor)}
        anchorEl={packageComplianceInfoAnchor}
        onClose={() => setPackageComplianceInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Typography style={{ padding: 16, maxWidth: 360 }}>
          This section summarises package compliance results. Compliance distributions and
          averages use only projects with a package compliance report.
        </Typography>
      </Popover>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography gutterBottom>Package Compliance Reports</Typography>
            <Typography variant="h4">
              {packageCompliance.total} / {metrics.totalProjects}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {metrics.totalProjects > 0
                ? `Found on ${Math.round((packageCompliance.total / metrics.totalProjects) * 100)}% of projects`
                : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography gutterBottom>Overall Project Compliance</Typography>
              <IconButton
                aria-label="More information about overall project compliance"
                size="small"
                onClick={(event) => setOverallComplianceInfoAnchor(event.currentTarget)}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
              <Popover
                open={Boolean(overallComplianceInfoAnchor)}
                anchorEl={overallComplianceInfoAnchor}
                onClose={() => setOverallComplianceInfoAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Typography style={{ padding: 16, maxWidth: 320 }}>
                  Each bar shows the number of package compliance reports with the selected
                  overall status. The average is the percentage of all projects with a package
                  compliance report that have that status.
                </Typography>
              </Popover>
            </Box>
            {packageCompliance.total > 0 ? (
              <Box
                display="flex"
                alignItems="flex-end"
                justifyContent="space-around"
                style={{ minHeight: 220, paddingBottom: 12 }}
              >
                {(
                  [
                    { label: 'Low', key: 'low', avg: 'averageLow', color: '#d32f2f' },
                    { label: 'Medium', key: 'medium', avg: 'averageMedium', color: '#fbc02d' },
                    { label: 'High', key: 'high', avg: 'averageHigh', color: '#2e7d32' },
                  ] as const
                ).map(({ label, key, avg, color }) => {
                  const maxValue = Math.max(
                    packageCompliance.low,
                    packageCompliance.medium,
                    packageCompliance.high,
                    1,
                  );
                  const barHeight = Math.max(
                    (packageCompliance[key] / maxValue) * 140,
                    packageCompliance[key] > 0 ? 18 : 0,
                  );

                  return (
                    <Box key={key} display="flex" flexDirection="column" alignItems="center" width="30%">
                      <Tooltip title={`${label}: ${packageCompliance[key]}`}>
                        <Box
                          width="60%"
                          borderRadius={4}
                          style={{ height: barHeight, backgroundColor: color, minHeight: packageCompliance[key] > 0 ? 18 : 0 }}
                        />
                      </Tooltip>
                      <Typography variant="h4" style={{ marginTop: 8, color }}>
                        {packageCompliance[key]}
                      </Typography>
                      <Typography variant="body1">{label}</Typography>
                      <Typography variant="caption" color="textSecondary">
                         {packageCompliance[avg] !== null
                          ? `${(packageCompliance[avg]! * 100).toFixed(2)}%`
                          : 'N/A'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="h4">N/A</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {/* Agentic application assessment */}
    <Box display="flex" alignItems="center" style={{ marginTop: 24 }}>
      <AssessmentTwoToneIcon style={{ marginRight: 8, marginBottom: 4 }} />
      <Typography variant="h6" gutterBottom>Agentic Application Assessment</Typography>
      <IconButton
        aria-label="More information about agentic evaluator"
        size="small"
        style={{ marginLeft: 4, marginBottom: 4 }}
        onClick={(event) => setAgenticInfoAnchor(event.currentTarget)}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(agenticInfoAnchor)}
        anchorEl={agenticInfoAnchor}
        onClose={() => setAgenticInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Typography style={{ padding: 16, maxWidth: 360 }}>
          This section presents AI-driven quality assessments performed by the agentic evaluator. It shows the average Architectural Fitness score (how closely components behave as declared in their architectural specifications) and the average Documentation Score (how well project repositories are documented), together with a breakdown of identified architectural findings grouped by category and severity.
        </Typography>
      </Popover>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography gutterBottom>Architectural Fitness</Typography>
                <IconButton
                  aria-label="More information about architectural fitness"
                  size="small"
                  onClick={(event) => setArchitecturalFitnessInfoAnchor(event.currentTarget)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
                <Popover
                  open={Boolean(architecturalFitnessInfoAnchor)}
                  anchorEl={architecturalFitnessInfoAnchor}
                  onClose={() => setArchitecturalFitnessInfoAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Typography style={{ padding: 16, maxWidth: 320 }}>
                    How closely the component behaves as declared in its architectural specifications.
                  </Typography>
                </Popover>
              </Box>

              <Typography variant="h4">
                {agenticEvaluator.averageArchitecturalFitness !== null
                  ? `${agenticEvaluator.averageArchitecturalFitness.toFixed(1)}%`
                  : 'N/A'}
              </Typography>
              {agenticEvaluator.averageArchitecturalFitness !== null && (
                <Box mt={1}>
                 <LinearProgress variant="determinate" value={Math.min(agenticEvaluator.averageArchitecturalFitness, 100)} />
                </Box>
              )}
              <Typography variant="caption" color="textSecondary">
               Overall average across {agenticEvaluator.withArchitecturalFitness} / {metrics.totalProjects} projects
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography gutterBottom>Documentation Score</Typography>
                <IconButton
                  aria-label="More information about documentation score"
                  size="small"
                  onClick={(event) => setDocumentationScoreInfoAnchor(event.currentTarget)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
                <Popover
                  open={Boolean(documentationScoreInfoAnchor)}
                  anchorEl={documentationScoreInfoAnchor}
                  onClose={() => setDocumentationScoreInfoAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Typography style={{ padding: 16, maxWidth: 320 }}>
                    How well the project repository is documented.
                  </Typography>
                </Popover>
              </Box>
              <Typography variant="h4">
                {agenticEvaluator.averageDocumentationScore !== null
                  ? `${agenticEvaluator.averageDocumentationScore.toFixed(1)}%`
                  : 'N/A'}
              </Typography>
              {agenticEvaluator.averageDocumentationScore !== null && (
                <Box mt={1}>
                 <LinearProgress variant="determinate" value={Math.min(agenticEvaluator.averageDocumentationScore, 100)} />
                </Box>
              )}
              <Typography variant="caption" color="textSecondary">
               Overall average across {agenticEvaluator.withDocumentationScore} / {metrics.totalProjects} projects
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Grid>

    {/* Architectural findings */}

      {agenticEvaluator.withWeakPointCounters > 0 && agenticEvaluator.weakPointCategories.length > 0 ? (
        <Grid item xs={12} sm={6} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ marginBottom: 16 }}>
                <Typography gutterBottom>Architectural Findings</Typography>
                <IconButton
                  aria-label="More information about architectural findings"
                  size="small"
                  onClick={(event) => setWeakPointsInfoAnchor(event.currentTarget)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
                <Popover
                  open={Boolean(weakPointsInfoAnchor)}
                  anchorEl={weakPointsInfoAnchor}
                  onClose={() => setWeakPointsInfoAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Typography style={{ padding: 16, maxWidth: 320 }}>
                    Architectural findings highlight areas for improvement identified by the architectural analysis. Colors indicate the severity level: high, medium, or low.
                  </Typography>
                </Popover>
              </Box>
              <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 16 }}>
                {WEAK_POINT_SEVERITY_ORDER.map((severity) => (
                  <Box key={severity} display="flex" alignItems="center" mr={2}>
                    <Box
                      width={12}
                      height={12}
                      mr={1}
                      borderRadius={2}
                      style={{ backgroundColor: WEAK_POINT_SEVERITY_COLOR[severity] }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {WEAK_POINT_SEVERITY_LABEL[severity]}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                {agenticEvaluator.weakPointCategories.map(({ category, label, total, severities }) => (
                  <Box key={category}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0}>
                      <Typography variant="body1">{label}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {severities
                          .filter(({ count }) => count > 0)
                          .map(({ severity, count }) => `${WEAK_POINT_SEVERITY_LABEL[severity]}: ${count}`)
                          .join(', ')}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                      <Box
                        display="flex"
                        flexGrow={1}
                        height={18}
                        borderRadius={9}
                        overflow="hidden"
                        style={{ backgroundColor: '#eceff1' }}
                      >
                        {severities.map(({ severity, count }) => {
                          if (count === 0 || total === 0) {
                            return null;
                          }

                          return (
                            <Tooltip
                              key={`${category}-${severity}`}
                              title={`${label} — ${WEAK_POINT_SEVERITY_LABEL[severity]}: ${count}`}
                            >
                              <Box
                                height="100%"
                                style={{
                                  width: `${(count / total) * 100}%`,
                                  backgroundColor: WEAK_POINT_SEVERITY_COLOR[severity],
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                      <Typography variant="h4">{total}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ) : (
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography gutterBottom>Architectural Findings</Typography>
              <Typography variant="h4">N/A</Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
    </Box>
  );
};

export default MetricsDisplay;
